import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { fetchMyPaymentsPage } from '../widget-api/my-payments';
import { useWidgetSeo } from '../widget-hooks/use-widget-seo';
import { PATH } from '../widget-lib/path-routes';
import { bucketPendingCheckout, isPaymentLinkExpired } from '../widget-lib/pay-checkout-pending';

type PendingPayment = {
  id: number;
  status: string;
  provider: string;
  amount_cents: number;
  currency: string;
  payment_url?: string | null;
  created_at: string;
};

function formatAmount(cents: number, currency: string): string {
  return new Intl.NumberFormat('ru-RU', { style: 'currency', currency, minimumFractionDigits: 2 }).format(cents / 100);
}

function statusLabel(st: string): string {
  switch (st) {
    case 'pending':
      return 'Ожидает оплаты';
    case 'processing':
      return 'Обрабатывается';
    case 'succeeded':
      return 'Оплачено';
    case 'cancelled':
      return 'Отменён';
    case 'failed':
      return 'Ошибка';
    default:
      return st;
  }
}

export default function PayCheckoutPage() {
  useWidgetSeo(
    'Оплата заказа',
    'Страница оформления и оплаты неоплаченных заказов и бронирований.'
  );
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  const suffix = qs ? `?${qs}` : '';
  const { accessToken, bootstrapHint, orgSlug, me } = useEmbedBootstrap();
  const [payments, setPayments] = useState<PendingPayment[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchPending = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    try {
      const page = await fetchMyPaymentsPage(accessToken, { limit: 50 });
      setPayments(
        page.items
          .filter((p) => p.status === 'pending')
          .map((p) => ({
            id: p.id,
            status: p.status,
            provider: p.provider,
            amount_cents: p.amount_cents,
            currency: p.currency,
            payment_url: p.payment_url,
            created_at: p.created_at
          }))
      );
    } catch {
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const { withLiveLink, pendingOther, archived } = bucketPendingCheckout(payments);
  const now = Date.now();

  const renderPendingItem = (p: PendingPayment, opts: { variant: 'live' | 'other' | 'archived'; linkExpired?: boolean }) => {
    const stale = opts.variant === 'archived';
    const showPay = opts.variant === 'live';
    const linkExpired = opts.variant === 'other' && (opts.linkExpired ?? isPaymentLinkExpired(p, now));
    return (
      <li
        key={p.id}
        className={`rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] p-4 text-sm ${stale ? 'opacity-90' : ''}`}
      >
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-medium text-[var(--text)]">{formatAmount(p.amount_cents, p.currency)}</p>
            <p className="text-xs text-[var(--text-muted)]">
              {statusLabel(p.status)} &middot; {p.provider}
            </p>
            <p className="text-xs text-[var(--text-muted)]">{new Date(p.created_at).toLocaleString('ru-RU')}</p>
            {linkExpired ? (
              <p className="mt-1 text-xs text-[var(--warn-text)]">
                Срок действия ссылки на оплату истёк. Оформите оплату заново из записи или каталога абонементов.
              </p>
            ) : null}
            {stale ? (
              <p className="text-xs text-[var(--text-muted)]">
                Создан более 3 суток назад — при необходимости создайте новую оплату в студии.
              </p>
            ) : null}
            {!showPay && !linkExpired && p.status === 'pending' && !(p.payment_url ?? '').trim() ? (
              <p className="mt-1 text-xs text-[var(--text-muted)]">Ссылка на шлюз ещё не сформирована или недоступна.</p>
            ) : null}
          </div>
          {showPay && p.status === 'pending' && p.payment_url ? (
            <a
              href={p.payment_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-8 shrink-0 items-center justify-center rounded-sm bg-[var(--btn-primary-bg)] px-3 text-[13px] font-medium text-[var(--btn-primary-fg)] hover:bg-[var(--btn-primary-bg-hover)]"
            >
              Оплатить
            </a>
          ) : null}
        </div>
      </li>
    );
  };

  if (bootstrapHint && !orgSlug) {
    return (
      <div className="space-y-3 w-full min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Оплата онлайн</h1>
        <p className="text-sm text-[var(--text-muted)]">{bootstrapHint}</p>
      </div>
    );
  }

  if (!accessToken) {
    return (
      <div className="space-y-4 w-full min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Оплата онлайн</h1>
        <p className="text-sm text-[var(--text-muted)]">Войдите, чтобы оплатить запись или покупку через платёжный шлюз.</p>
        <div className="flex flex-wrap gap-2">
          <Link to={`${PATH.signIn}${suffix}`}
            className="inline-flex h-9 items-center justify-center rounded-sm bg-[var(--btn-primary-bg)] px-3.5 text-[13px] font-medium text-[var(--btn-primary-fg)] hover:bg-[var(--btn-primary-bg-hover)]">
            Войти
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full min-w-0">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Оплата онлайн</h1>
        <p className="mt-1 text-sm text-[var(--text-muted)]">
          {me ? (me.full_name || 'Пользователь') : ''}
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-[var(--text-muted)]">Загрузка...</p>
      ) : payments.length === 0 ? (
        <div className="space-y-3">
          <p className="text-sm text-[var(--text-muted)]">Нет платежей, ожидающих оплаты.</p>
          <div className="flex flex-wrap gap-2">
            <Link to={`${PATH.schedule}${suffix}`}
              className="inline-flex h-9 items-center justify-center rounded-sm bg-[var(--btn-primary-bg)] px-3.5 text-[13px] font-medium text-[var(--btn-primary-fg)] hover:bg-[var(--btn-primary-bg-hover)]">
              Записаться на занятие
            </Link>
            <Link to={`${PATH.subscriptionPlans}${suffix}`}
              className="inline-flex h-9 items-center justify-center rounded-sm border border-[var(--btn-secondary-border)] bg-[var(--btn-secondary-bg)] px-3.5 text-[13px] font-medium text-[var(--btn-secondary-fg)] hover:bg-[var(--btn-secondary-bg-hover)]">
              Купить абонемент
            </Link>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {withLiveLink.length > 0 ? (
            <>
              <p className="text-xs font-medium text-[var(--text-muted)]">Активная ссылка на оплату</p>
              <ul className="space-y-3">{withLiveLink.map((p) => renderPendingItem(p, { variant: 'live' }))}</ul>
            </>
          ) : null}
          {pendingOther.length > 0 ? (
            <>
              <p className="text-xs font-medium text-[var(--text-muted)]">Ожидание без активной ссылки</p>
              <ul className="space-y-3">{pendingOther.map((p) => renderPendingItem(p, { variant: 'other', linkExpired: isPaymentLinkExpired(p, now) }))}</ul>
            </>
          ) : null}
          {archived.length > 0 ? (
            <details className="rounded-xl border border-[var(--border)] bg-[var(--bg-elevated)] p-3 text-sm">
              <summary className="cursor-pointer font-medium text-[var(--text)]">
                Старые ожидания оплаты ({archived.length})
              </summary>
              <ul className="mt-3 space-y-3">{archived.map((p) => renderPendingItem(p, { variant: 'archived' }))}</ul>
            </details>
          ) : null}
        </div>
      )}

      <div className="border-t border-[var(--border)] pt-4">
        <details className="group text-sm">
          <summary className="cursor-pointer text-[var(--text-muted)] hover:text-[var(--text)]">Другие способы оплаты</summary>
          <div className="mt-3 space-y-2 pl-2">
            <p className="text-[var(--text-muted)]">
              <Link className="text-[var(--accent)] underline-offset-2 hover:underline" to={`${PATH.payLoyalty}${suffix}`}>
                Оплата с абонемента
              </Link>
            </p>
            <p className="text-[var(--text-muted)]">
              <Link className="text-[var(--accent)] underline-offset-2 hover:underline" to={`${PATH.bookings}${suffix}`}>
                Мои записи
              </Link>
            </p>
          </div>
        </details>
      </div>
    </div>
  );
}
